import type { EditHistory } from '../core/history';
import type { AppStore } from '../core/store';

/** A local editor with its own undo while it is open (geometry editing session, vertex editor). */
export interface LocalUndo {
  readonly name: string;
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  undo(): void;
  redo(): void;
}

export interface HistoryHooks {
  /** The open local editor, which receives Deshacer / Rehacer instead of the project history. */
  local(): LocalUndo | null;
  /** Why the project history cannot be used now (e.g. unapplied changes in Apariencia), or null. */
  blocked(): string | null;
  /** Message after a step («Deshecho: clasificación»). */
  notify(message: string): void;
}

const MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform ?? '');
export const UNDO_SHORTCUT = MAC ? '⌘Z' : 'Ctrl+Z';
export const REDO_SHORTCUT = MAC ? '⇧⌘Z' : 'Ctrl+Y';

/** Text the browser undoes natively: Ctrl+Z there belongs to the field, never to the project. */
export const editsText = (target: EventTarget | null): boolean => {
  const element = target instanceof Element ? target : null;
  if (!element) return false;
  if (element.closest('textarea, select, [contenteditable=""], [contenteditable="true"]')) return true;
  const input = element.closest('input');
  return Boolean(input && !['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color', 'file'].includes(input.type));
};

/**
 * V1.9.0 E5 · Deshacer / Rehacer: the toolbar and menu buttons ([data-history]) and the keyboard (Ctrl+Z, Ctrl+Y and
 * Ctrl+Shift+Z; ⌘ on Mac). Inside a text field the browser's own undo is left alone. While the geometry editing
 * session or the vertex editor is open, its own undo is used, so the two never mix.
 */
export class HistoryController {
  private readonly listeners = new AbortController();
  private readonly unsubscribe: (() => void)[] = [];

  constructor(private readonly store: AppStore, private readonly history: EditHistory, private readonly hooks: HistoryHooks) {
    const signal = this.listeners.signal;
    document.addEventListener('click', (event) => {
      const button = (event.target as Element | null)?.closest<HTMLButtonElement>('[data-history]');
      if (!button || button.disabled) return;
      event.preventDefault();
      if (button.dataset.history === 'redo') this.redo(); else this.undo();
    }, { signal });
    document.addEventListener('keydown', (event) => {
      if (event.defaultPrevented || event.altKey || !(event.ctrlKey || event.metaKey)) return;
      const key = event.key.toLowerCase();
      const redo = key === 'y' || (key === 'z' && event.shiftKey); const undo = key === 'z' && !event.shiftKey;
      if (!undo && !redo) return;
      if (editsText(event.target) || document.querySelector('dialog[open]')) return;
      event.preventDefault();
      if (redo) this.redo(); else this.undo();
    }, { signal });
    // The vertex editor changes without touching the store: refresh the buttons after each gesture.
    document.addEventListener('pointerup', () => queueMicrotask(() => this.render()), { signal });
    this.unsubscribe.push(history.subscribe(() => this.render()));
    this.unsubscribe.push(store.subscribe((state, previous) => { if (state.geometryEditSession !== previous.geometryEditSession) this.render(); }));
    this.render();
  }

  destroy(): void { this.listeners.abort(); this.unsubscribe.forEach((stop) => stop()); }

  undo(): void {
    const local = this.hooks.local();
    if (local) { if (local.canUndo) local.undo(); this.render(); return; }
    const blocked = this.hooks.blocked(); if (blocked) { this.hooks.notify(blocked); return; }
    const label = this.history.undoLabel;
    if (this.store.undo()) this.hooks.notify(`Deshecho: ${label}`);
  }

  redo(): void {
    const local = this.hooks.local();
    if (local) { if (local.canRedo) local.redo(); this.render(); return; }
    const blocked = this.hooks.blocked(); if (blocked) { this.hooks.notify(blocked); return; }
    const label = this.history.redoLabel;
    if (this.store.redo()) this.hooks.notify(`Rehecho: ${label}`);
  }

  /** Enabled state and the name of the step in each button's tooltip. */
  render(): void {
    const local = this.hooks.local();
    const state = local
      ? { undo: local.canUndo, redo: local.canRedo, undoTitle: `Deshacer en ${local.name}`, redoTitle: `Rehacer en ${local.name}` }
      : {
        undo: this.history.canUndo, redo: this.history.canRedo,
        undoTitle: this.history.undoLabel ? `Deshacer: ${this.history.undoLabel}` : 'Nada que deshacer',
        redoTitle: this.history.redoLabel ? `Rehacer: ${this.history.redoLabel}` : 'Nada que rehacer',
      };
    document.querySelectorAll<HTMLButtonElement>('[data-history]').forEach((button) => {
      const redo = button.dataset.history === 'redo';
      button.disabled = !(redo ? state.redo : state.undo);
      const title = `${redo ? state.redoTitle : state.undoTitle} (${redo ? REDO_SHORTCUT : UNDO_SHORTCUT})`;
      button.title = title;
      if (button.classList.contains('tool-button')) button.setAttribute('aria-label', title);
    });
  }
}
